import { ForwardedRef, forwardRef, useImperativeHandle, useRef } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Flex } from '@revenuemarket/cds';
import { colors, theme } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { Typography } from '@revenuemarket/ui';
import { ICellRendererComp, ICellRendererParams } from 'ag-grid-community';
import { Select } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';

import { LabeledBankAccountTransaction } from '~apis/cash-flow-overview/transactions/getTransactions';
import TransactionMappingModal from '~components/Mapping/TransactionMappingModal';
import { useGroupLabels } from '~hooks/queries/custom-labels/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { getSplittedText } from '~utils/transaction/getSplittedText';

interface Props extends ICellRendererParams<LabeledBankAccountTransaction> {
  options: DefaultOptionType[];
  agGridRowData: LabeledBankAccountTransaction[];
}

const AGGridCustomLabelRenderer = (
  { value, options, data, agGridRowData }: Props,
  ref: ForwardedRef<ICellRendererComp>,
) => {
  const { companyId } = useDemoAwareCompanyId();
  const { data: customLabels } = useGroupLabels({ companyId });
  const customLabelData = customLabels?.Data?.data;

  // FIXME: transaction 업데이트 안되는 이슈
  const labelCount =
    agGridRowData?.find((row) => row.transactionId === data?.transactionId)?.labelCount ?? 0;
  const isSplitted = labelCount > 1;

  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      getGui: () => containerRef.current ?? document.body,
      refresh: () => true,
    }),
    [],
  );

  if (!data) {
    return null;
  }

  if (isSplitted) {
    return (
      <Flex
        align="center"
        style={{ height: '100%', paddingLeft: '8px' }}
        onClick={(e) => {
          e.stopPropagation();
          // TODO: 분할 거래내역 시에만 click 이벤트
          overlays.open(({ isOpen, onClose }) => (
            <TransactionMappingModal
              open={isOpen}
              onCancel={onClose}
              transactionId={data.transactionId}
              showTaxInvoice={false}
            />
          ));
        }}
      >
        <Typography variant="caption1" color={colors.grayscale400} useEllipsis>
          {getSplittedText({
            category: customLabelData?.find((c) => c.id === data.customLabelId)?.name,
            labelCount: data?.groupLabelCount ?? 0,
            allCount: labelCount,
          })}
        </Typography>
      </Flex>
    );
  }

  return (
    <Flex ref={containerRef} align="center" style={{ height: '100%' }}>
      <Select
        value={value}
        options={options}
        open={false}
        bordered={false}
        showSearch={false}
        searchValue=""
        style={{
          width: '100%',
        }}
        suffixIcon={<DownOutlined style={{ color: theme.colorTokens.iconPlaceholder }} />}
        className={css`
          &.ant-select .ant-select-selector .ant-select-selection-item {
            font-size: ${theme.typography.body1.size}px;
          }
        `}
      />
    </Flex>
  );
};

export default forwardRef(AGGridCustomLabelRenderer);
